import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { TileStatusIcon, scanError, useStartScan, type TileStatus } from '../components/site.js';
import { Button, Card, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime, formatNumber } from '../i18n.js';
import { useMe } from '../session.js';

/** Core Web Vitals thresholds (good, poor) as Google defines them. */
const LIMITS = {
  score: [90, 50],
  lcpMs: [2500, 4000],
  inpMs: [200, 500],
  tbtMs: [200, 600],
  cls: [0.1, 0.25],
  ttfbMs: [800, 1800],
} as const;
type Metric = keyof typeof LIMITS;

export function rate(metric: Metric, value: number | null): TileStatus {
  if (value === null) return 'unknown';
  const [good, poor] = LIMITS[metric];
  // The score is better when higher, all timings when lower.
  if (metric === 'score') return value >= good ? 'good' : value >= poor ? 'warn' : 'bad';
  return value <= good ? 'good' : value <= poor ? 'warn' : 'bad';
}

function Value({
  label,
  metric,
  value,
  text,
}: {
  label: string;
  metric: Metric;
  value: number | null;
  text: string;
}) {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <dt className="text-sm text-slate-700">{label}</dt>
      <dd className="text-xl font-semibold">{value === null ? '–' : text}</dd>
      <dd>
        <TileStatusIcon status={rate(metric, value)} />
      </dd>
    </div>
  );
}

function SnapshotCard({ snap }: { snap: Schemas['PerformanceSnapshot'] }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const lang = i18n.language;
  const seconds = (ms: number | null) => `${formatNumber((ms ?? 0) / 1000, lang)} s`;
  const lcp = snap.field?.lcpMs ?? snap.lab.lcpMs;
  const cls = snap.field?.cls ?? snap.lab.cls;
  return (
    <li>
      <Card>
        <h2 className="break-all text-lg font-semibold">{snap.url}</h2>
        <p className="mb-3 text-sm text-slate-700">
          {t(`speed.strategy.${snap.strategy}`)} ·{' '}
          {t('speed.measuredAt', { time: formatDateTime(snap.measuredAt, lang, timeZone) })}
          {snap.field ? ` · ${t('speed.fieldData')}` : ` · ${t('speed.labData')}`}
        </p>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Value
            label={t('speed.score')}
            metric="score"
            value={snap.score}
            text={`${snap.score ?? 0}/100`}
          />
          <Value label={t('speed.lcp')} metric="lcpMs" value={lcp} text={seconds(lcp)} />
          {snap.field && snap.field.inpMs !== null ? (
            <Value
              label={t('speed.inp')}
              metric="inpMs"
              value={snap.field.inpMs}
              text={`${snap.field.inpMs} ms`}
            />
          ) : (
            <Value
              label={t('speed.tbt')}
              metric="tbtMs"
              value={snap.lab.tbtMs}
              text={`${snap.lab.tbtMs ?? 0} ms`}
            />
          )}
          <Value
            label={t('speed.cls')}
            metric="cls"
            value={cls}
            text={formatNumber(cls ?? 0, lang, 2)}
          />
          <Value
            label={t('speed.ttfb')}
            metric="ttfbMs"
            value={snap.lab.ttfbMs}
            text={`${snap.lab.ttfbMs ?? 0} ms`}
          />
          {snap.pageBytes !== null ? (
            <div className="rounded-md border border-slate-200 p-3">
              <dt className="text-sm text-slate-700">{t('speed.weight')}</dt>
              <dd className="text-xl font-semibold">
                {formatNumber(snap.pageBytes / 1_048_576, lang)} MB
              </dd>
            </div>
          ) : null}
        </dl>
        {snap.topOpportunities.length > 0 ? (
          <div className="mt-4">
            <h3 className="mb-2 font-semibold">{t('speed.opportunities')}</h3>
            <ul className="flex flex-col gap-1">
              {snap.topOpportunities.map((o) => (
                <li key={o.id} className="flex flex-wrap justify-between gap-2">
                  <span>{t(`speed.audits.${o.id}`, { defaultValue: o.id })}</span>
                  <span className="text-slate-700">
                    {o.savingsMs > 0
                      ? t('speed.saves', { value: seconds(o.savingsMs) })
                      : t('speed.saves', { value: `${Math.round(o.savingsBytes / 1024)} KB` })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>
    </li>
  );
}

/** Page speed (PageSpeed Insights): Core Web Vitals with traffic lights and top savings. */
export function Speed() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const scans = useQuery({
    queryKey: ['site', siteId, 'scans', 'performance'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/scans', {
          params: { path: { siteId }, query: { type: 'performance', limit: 1 } },
        }),
      ),
    refetchInterval: (query) => {
      const s = query.state.data?.items[0]?.status;
      return s === 'queued' || s === 'running' ? 5000 : false;
    },
  });
  const latestScan = scans.data?.items[0];
  const running = latestScan?.status === 'queued' || latestScan?.status === 'running';
  const perf = useQuery({
    queryKey: ['site', siteId, 'performance', latestScan?.status],
    queryFn: () => unwrap(api.GET('/sites/{siteId}/performance', { params: { path: { siteId } } })),
  });
  const start = useStartScan(siteId, 'performance');
  const mobileFirst = [...(perf.data?.latest ?? [])].sort((a, b) =>
    a.strategy === b.strategy ? 0 : a.strategy === 'mobile' ? -1 : 1,
  );

  return (
    <>
      <PageTitle
        actions={
          <Button busy={start.isPending} disabled={running} onClick={() => start.mutate()}>
            {t('speed.measure')}
          </Button>
        }
      >
        {t('speed.title')}
      </PageTitle>
      <div className="mb-4 flex flex-col gap-3">
        {start.isError ? <Notice kind="error">{scanError(start.error, t)}</Notice> : null}
        {running ? <Notice kind="info">{t('speed.running')}</Notice> : null}
        {latestScan?.status === 'failed' && latestScan.errorCode ? (
          <Notice kind="warning">
            {t(`speed.errors.${latestScan.errorCode}`, { defaultValue: t('speed.errors.other') })}
          </Notice>
        ) : null}
      </div>
      {perf.isPending ? <Loading /> : null}
      {perf.isError ? (
        <LoadError message={errorMessage(perf.error, t)} onRetry={() => void perf.refetch()} />
      ) : null}
      {perf.data && mobileFirst.length === 0 ? (
        <Notice kind="info">{t('speed.none')}</Notice>
      ) : null}
      {mobileFirst.length > 0 ? (
        <>
          <ul className="flex flex-col gap-4">
            {mobileFirst.map((s) => (
              <SnapshotCard key={`${s.url}-${s.strategy}`} snap={s} />
            ))}
          </ul>
          <p className="mt-4 text-sm text-slate-700">{t('speed.source')}</p>
        </>
      ) : null}
    </>
  );
}
